import React, { useState } from 'react';
import { collection, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface Student {
  id: string;
  enrollmentId?: string; // Enrollment ID from bulk upload
  name: string;
  class: string;
  section: string;
  rollNumber: string;
  email: string;
  phone: string;
  className?: string; // New field: "Class 1A" format
  dateOfBirth?: string;
  address?: string;
  parentName?: string;
  parentPhone?: string;
  house?: string; // House from bulk upload
}

interface StudentDataProps {
  domain: string;
}

const StudentData: React.FC<StudentDataProps> = ({ domain }) => {
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [classes, setClasses] = useState<string[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<Student>>({});
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);

  // Load available classes on mount
  React.useEffect(() => {
    if (domain) {
      loadAvailableClasses();
    }
  }, [domain]);

  const loadAvailableClasses = async () => {
    try {
      const studentsRef = collection(db, `school-users/${domain}/students`);
      const snapshot = await getDocs(studentsRef);

      const classSet = new Set<string>();
      snapshot.forEach((doc) => {
        const studentData = doc.data();
        if (studentData.class) {
          classSet.add(String(studentData.class));
        }
      });

      setClasses(Array.from(classSet).sort());
    } catch (error) {
      console.error('Error loading classes:', error);
    }
  };

  const loadStudentsByClass = async (classValue: string) => {
    if (!domain || !classValue) {
      setStudents([]);
      return;
    }

    setLoading(true);
    setError('');

    console.log('📚 Loading students for class:', classValue, 'domain:', domain);

    try {
      const studentsRef = collection(db, `school-users/${domain}/students`);
      const snapshot = await getDocs(studentsRef);

      const studentsList: Student[] = [];

      snapshot.forEach((doc) => {
        const studentData = { id: doc.id, ...doc.data() } as Student;
        // Filter by selected class
        if (String(studentData.class) === classValue) {
          studentsList.push(studentData);
        }
      });

      // Sort manually with safe comparison
      studentsList.sort((a, b) => {
        const rollA = String(a.rollNumber || '');
        const rollB = String(b.rollNumber || '');
        return rollA.localeCompare(rollB);
      });

      setStudents(studentsList);

      if (studentsList.length === 0) {
        setError(`No students found in Class ${classValue}`);
      } else {
        console.log('✅ Successfully loaded', studentsList.length, 'students for Class', classValue);
      }
    } catch (error: any) {
      console.error('❌ Error loading students:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);

      let errorMessage = `Failed to load students: ${error.message || 'Unknown error'}`;

      if (error.code === 'permission-denied') {
        errorMessage = '🔒 Permission denied. Please check:\n' +
                      '1. Firestore security rules allow access\n' +
                      '2. You are logged in correctly\n' +
                      '3. Your user document has the correct domain field\n\n' +
                      'See FIRESTORE_SETUP.md for detailed instructions.';
      } else if (error.code === 'failed-precondition') {
        errorMessage = '⚠️ Index required. The query requires an index. Firebase should show a link to create it.';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClassChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const classValue = e.target.value;
    setSelectedClass(classValue);
    setSearchTerm(''); // Reset search when changing class
    if (classValue) {
      loadStudentsByClass(classValue);
    } else {
      setStudents([]);
    }
  };

  const filteredStudents = React.useMemo(() => {
    try {
      return students.filter(student => {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          String(student.name || '').toLowerCase().includes(searchLower) ||
          String(student.rollNumber || '').toLowerCase().includes(searchLower) ||
          String(student.email || '').toLowerCase().includes(searchLower);
        return matchesSearch;
      });
    } catch (error) {
      console.error('Error filtering students:', error);
      return students;
    }
  }, [students, searchTerm]);

  // View student details
  const handleViewStudent = (student: Student) => {
    setSelectedStudent(student);
    setShowDetailsModal(true);
  };

  // Open edit modal
  const handleEditStudent = (student: Student) => {
    setSelectedStudent(student);
    setEditFormData({ ...student });
    setShowEditModal(true);
  };

  // Update student
  const handleUpdateStudent = async () => {
    if (!selectedStudent) return;

    try {
      setLoading(true);
      const studentRef = doc(db, `school-users/${domain}/students`, selectedStudent.id);

      // Ensure className field is updated if class or section changed
      const updatedData = { ...editFormData };
      if (editFormData.class && editFormData.section) {
        updatedData.className = `Class ${editFormData.class}${editFormData.section}`;
      }

      await updateDoc(studentRef, updatedData);

      alert('Student updated successfully!');
      setShowEditModal(false);
      loadStudentsByClass(selectedClass); // Refresh list
    } catch (error: any) {
      console.error('Error updating student:', error);
      alert(`Failed to update student: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Delete single student
  const handleDeleteStudent = async (student: Student) => {
    if (!confirm(`Are you sure you want to delete ${student.name}? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeleting(true);
      const studentRef = doc(db, `school-users/${domain}/students`, student.id);
      await deleteDoc(studentRef);

      // Also delete user profile from /users collection if enrollment ID exists
      if (student.enrollmentId) {
        try {
          // Delete user profile directly using enrollmentId as document ID
          const userDocRef = doc(db, 'users', student.enrollmentId);
          await deleteDoc(userDocRef);

          console.log(`Deleted user profile for ${student.enrollmentId}`);
        } catch (userError) {
          console.warn('Error deleting user profile:', userError);
          // Continue even if user deletion fails
        }
      }

      alert('Student deleted successfully!');
      loadStudentsByClass(selectedClass); // Refresh list
    } catch (error: any) {
      console.error('Error deleting student:', error);
      alert(`Failed to delete student: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // Bulk delete students
  const handleBulkDelete = async () => {
    if (selectedStudents.size === 0) {
      alert('Please select students to delete');
      return;
    }

    if (!confirm(`Are you sure you want to delete ${selectedStudents.size} student(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeleting(true);

      // Get full student objects for selected IDs to access enrollment IDs
      const selectedStudentObjects = students.filter(s => selectedStudents.has(s.id));

      // Delete students from school-users collection
      const deletePromises = Array.from(selectedStudents).map(studentId => {
        const studentRef = doc(db, `school-users/${domain}/students`, studentId);
        return deleteDoc(studentRef);
      });

      await Promise.all(deletePromises);

      // Delete associated user profiles from /users collection
      const userDeletePromises = selectedStudentObjects
        .filter(student => student.enrollmentId)
        .map(async (student) => {
          try {
            // Delete user profile directly using enrollmentId as document ID
            const userDocRef = doc(db, 'users', student.enrollmentId!);
            await deleteDoc(userDocRef);
            console.log(`Deleted user profile for ${student.enrollmentId}`);
          } catch (userError) {
            console.warn(`Error deleting user profile for ${student.enrollmentId}:`, userError);
            // Continue even if user deletion fails
          }
        });

      await Promise.all(userDeletePromises);

      alert(`Successfully deleted ${selectedStudents.size} student(s)!`);
      setSelectedStudents(new Set());
      loadStudentsByClass(selectedClass); // Refresh list
    } catch (error: any) {
      console.error('Error bulk deleting students:', error);
      alert(`Failed to delete students: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  // Toggle student selection
  const toggleStudentSelection = (studentId: string) => {
    const newSelection = new Set(selectedStudents);
    if (newSelection.has(studentId)) {
      newSelection.delete(studentId);
    } else {
      newSelection.add(studentId);
    }
    setSelectedStudents(newSelection);
  };

  // Select all students
  const toggleSelectAll = () => {
    if (selectedStudents.size === filteredStudents.length) {
      setSelectedStudents(new Set());
    } else {
      setSelectedStudents(new Set(filteredStudents.map(s => s.id)));
    }
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <div className="row align-items-center g-3">
          <div className="col-md-4">
            <h5 className="mb-0">
              <i className="bi bi-people-fill me-2 text-primary-custom"></i>
              Student List
            </h5>
          </div>
          <div className="col-md-8">
            <div className="d-flex gap-2">
              <select
                className="form-select"
                style={{ maxWidth: '200px' }}
                value={selectedClass}
                onChange={handleClassChange}
                disabled={loading}
              >
                <option value="">-- Select Class --</option>
                {classes.map((cls) => (
                  <option key={cls} value={cls}>Class {cls}</option>
                ))}
              </select>
              <input
                type="text"
                className="form-control"
                placeholder="Search by name or roll number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={!selectedClass || loading}
              />
              {selectedStudents.size > 0 && (
                <button
                  className="btn btn-danger"
                  onClick={handleBulkDelete}
                  disabled={deleting}
                >
                  <i className="bi bi-trash me-2"></i>
                  Delete ({selectedStudents.size})
                </button>
              )}
              {loading && (
                <div className="d-flex align-items-center px-3">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-warning m-3 mb-0" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: '40px' }}>
                  {filteredStudents.length > 0 && (
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={selectedStudents.size === filteredStudents.length && filteredStudents.length > 0}
                      onChange={toggleSelectAll}
                    />
                  )}
                </th>
                <th>Enrollment ID</th>
                <th>Roll No</th>
                <th>Name</th>
                <th>Class</th>
                <th>Section</th>
                <th>House</th>
                <th>Email</th>
                <th>Phone</th>
                <th style={{ width: '180px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.length > 0 ? (
                filteredStudents.map((student) => (
                  <tr key={student.id}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedStudents.has(student.id)}
                        onChange={() => toggleStudentSelection(student.id)}
                      />
                    </td>
                    <td>{String(student.enrollmentId || '-')}</td>
                    <td>{String(student.rollNumber || '-')}</td>
                    <td>{String(student.name || 'N/A')}</td>
                    <td>{String(student.class || '-')}</td>
                    <td>{String(student.section || '-')}</td>
                    <td>{String(student.house || '-')}</td>
                    <td>{String(student.email || '-')}</td>
                    <td>{String(student.phone || '-')}</td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <button
                          className="btn btn-outline-primary"
                          title="View Details"
                          onClick={() => handleViewStudent(student)}
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                        <button
                          className="btn btn-outline-secondary"
                          title="Edit Student"
                          onClick={() => handleEditStudent(student)}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-outline-danger"
                          title="Delete Student"
                          onClick={() => handleDeleteStudent(student)}
                          disabled={deleting}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} className="text-center text-muted py-5">
                    {!selectedClass ? (
                      <div>
                        <i className="bi bi-card-list fs-1 d-block mb-3 text-primary-custom"></i>
                        <p className="mb-0">Select a class from the dropdown to view students</p>
                      </div>
                    ) : (
                      searchTerm ? 'No students match your search criteria' : 'No students found in this class'
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {selectedClass && students.length > 0 && (
        <div className="card-footer bg-white">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <span className="text-muted">
                Showing {filteredStudents.length} of {students.length} students
              </span>
              <span className="badge bg-primary-custom ms-2">
                Class {selectedClass}
              </span>
              {selectedStudents.size > 0 && (
                <span className="badge bg-info ms-2">
                  {selectedStudents.size} selected
                </span>
              )}
            </div>
            <button
              className="btn btn-primary"
              onClick={() => loadStudentsByClass(selectedClass)}
              disabled={loading}
            >
              <i className="bi bi-arrow-clockwise me-2"></i>Refresh
            </button>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showDetailsModal && selectedStudent && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-primary-custom text-white">
                <h5 className="modal-title">
                  <i className="bi bi-person-circle me-2"></i>
                  Student Details
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowDetailsModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="fw-bold text-muted small">Name</label>
                    <p className="mb-0">{selectedStudent.name || '-'}</p>
                  </div>
                  <div className="col-md-6">
                    <label className="fw-bold text-muted small">Enrollment ID</label>
                    <p className="mb-0">{selectedStudent.enrollmentId || '-'}</p>
                  </div>
                  <div className="col-md-3">
                    <label className="fw-bold text-muted small">Roll Number</label>
                    <p className="mb-0">{selectedStudent.rollNumber || '-'}</p>
                  </div>
                  <div className="col-md-3">
                    <label className="fw-bold text-muted small">Class/Section</label>
                    <p className="mb-0">{selectedStudent.class} - {selectedStudent.section}</p>
                  </div>
                  <div className="col-md-3">
                    <label className="fw-bold text-muted small">House</label>
                    <p className="mb-0">{selectedStudent.house || '-'}</p>
                  </div>
                  <div className="col-md-3">
                    <label className="fw-bold text-muted small">Date of Birth</label>
                    <p className="mb-0">{selectedStudent.dateOfBirth || '-'}</p>
                  </div>
                  <div className="col-md-6">
                    <label className="fw-bold text-muted small">Email</label>
                    <p className="mb-0">{selectedStudent.email || '-'}</p>
                  </div>
                  <div className="col-md-6">
                    <label className="fw-bold text-muted small">Phone</label>
                    <p className="mb-0">{selectedStudent.phone || '-'}</p>
                  </div>
                  <div className="col-md-6">
                    <label className="fw-bold text-muted small">Parent Name</label>
                    <p className="mb-0">{selectedStudent.parentName || '-'}</p>
                  </div>
                  <div className="col-md-6">
                    <label className="fw-bold text-muted small">Parent Phone</label>
                    <p className="mb-0">{selectedStudent.parentPhone || '-'}</p>
                  </div>
                  <div className="col-md-12">
                    <label className="fw-bold text-muted small">Address</label>
                    <p className="mb-0">{selectedStudent.address || '-'}</p>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleEditStudent(selectedStudent);
                  }}
                >
                  <i className="bi bi-pencil me-2"></i>
                  Edit Student
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Student Modal */}
      {showEditModal && selectedStudent && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-primary-custom text-white">
                <h5 className="modal-title">
                  <i className="bi bi-pencil me-2"></i>
                  Edit Student
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowEditModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editFormData.name || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Enrollment ID</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editFormData.enrollmentId || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, enrollmentId: e.target.value })}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Roll Number *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editFormData.rollNumber || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, rollNumber: e.target.value })}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Section *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editFormData.section || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, section: e.target.value })}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">House</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editFormData.house || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, house: e.target.value })}
                      placeholder="e.g., Red, Blue, Green"
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Date of Birth</label>
                    <input
                      type="date"
                      className="form-control"
                      value={editFormData.dateOfBirth || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, dateOfBirth: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={editFormData.email || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      className="form-control"
                      value={editFormData.phone || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Parent Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editFormData.parentName || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, parentName: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Parent Phone</label>
                    <input
                      type="tel"
                      className="form-control"
                      value={editFormData.parentPhone || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, parentPhone: e.target.value })}
                    />
                  </div>
                  <div className="col-md-12">
                    <label className="form-label">Address</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={editFormData.address || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUpdateStudent}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentData;